import { LazyWafBlockModal } from '#components'
import { shouldHandleSessionExpiry } from '~/utils/sessionExpiry'

export default defineNuxtPlugin(() => {
  const originalFetch = globalThis.$fetch
  let sessionExpiredHandled = false

  const handleWafResponse = (request: string | Request, response: Response) => {
    const rayId = response.headers.get('cf-ray')
    const mitigatedHeader = response.headers.get('cf-mitigated')

    const mitigatedValues = [
      'challenge',
      'block',
      'jschallenge',
      'managed-challenge',
    ]
    const isMitigated =
      mitigatedHeader && mitigatedValues.includes(mitigatedHeader.toLowerCase())

    const rayIdRegex = /^[a-fA-F0-9]+-[A-Z]{3}$/
    const isValidRayId = rayId && rayIdRegex.test(rayId)

    if (response.status !== 403 || !isMitigated) return

    const displayRayId = isValidRayId ? rayId : null
    const overlay = useOverlay()
    const { $groundcover } = useNuxtApp()

    const requestUrl =
      typeof request === 'string'
        ? request
        : (request as Request).url || request.toString()

    let cleanUrl = requestUrl
    try {
      const parsed = new URL(requestUrl, window.location.origin)
      cleanUrl =
        parsed.origin === window.location.origin
          ? parsed.pathname
          : `${parsed.origin}${parsed.pathname}`
    } catch {
      // Fallback to unparsed requestUrl if URL parsing fails
    }

    const rawRayId = displayRayId?.split('-')[0]
    const modal = overlay.create(LazyWafBlockModal, {
      props: {
        rayId: rawRayId,
        method:
          typeof request === 'string'
            ? 'GET'
            : (request as Request).method || 'GET',
        url: cleanUrl,
      },
    })
    modal.open()

    $groundcover?.captureException(new Error('waf_403_block'), {
      rayId: displayRayId || 'Unknown',
      path: cleanUrl,
    })
  }

  globalThis.$fetch = originalFetch.create({
    async onResponseError({ request, response }) {
      const route = useRoute()

      if (
        shouldHandleSessionExpiry({
          status: response.status,
          isPublicRoute: Boolean(route.meta.public),
          alreadyHandled: sessionExpiredHandled,
        })
      ) {
        sessionExpiredHandled = true

        const { $groundcover } = useNuxtApp()
        $groundcover.sendCustomEvent({
          event: 'session-expiry-redirect',
          attributes: { path: route.path },
        })

        const toast = useToast()
        toast.add({
          title: 'Session expired',
          description: 'Please log in again to continue.',
          color: 'error',
          duration: 0,
        })

        const { clear } = useUserSession()
        try {
          await clear()
        } catch {
          // Session clear can fail if the DELETE 500s — redirect regardless,
          // the session is expired either way.
        }
        await navigateTo('/welcome')
        return
      }

      handleWafResponse(request, response)
    },
  })

  return {
    provide: {
      handleWafResponse: (response: Response, request: string) =>
        handleWafResponse(request, response),
    },
  }
})
